import type { ParsedActionCommand } from './types';

/**
 * Tokenize a raw `GlobalMapSearch` input into a `ParsedActionCommand`.
 * Pure function — call it as often as you like (every keystroke is
 * fine).
 *
 * Behavior (note the leading prefix is two slashes, not one):
 *   - `//`             → empty `name`, no args. Surface the full
 *                        action catalog from this state.
 *   - `//foo`          → `name = 'foo'`, no args.
 *   - `//foo `         → `name = 'foo'`, no args, `trailingSpace = true`.
 *                        Used to detect "user is past the name and into
 *                        args" without needing a non-whitespace arg
 *                        character to be typed first.
 *   - `//foo bar baz`  → `name = 'foo'`, `args = ['bar', 'baz']`,
 *                        `rest = 'bar baz'`.
 *
 * Inputs not starting with `//` should never reach this — the host is
 * expected to gate on that — but for safety the parser still produces
 * a deterministic empty-shape result instead of throwing.
 */
export function parseActionCommand(input: string): ParsedActionCommand {
  const raw = input;

  if (!input.startsWith('//')) {
    return {
      raw,
      name: '',
      args: [],
      rest: '',
      trailingSpace: false,
    };
  }

  // Drop the leading `//`. Same trimming caveat as the slash parser:
  // a single trailing space after the name is meaningful
  // (`trailingSpace`) and trimming would erase it.
  const body = input.slice(2);

  const nameMatch = body.match(/^(\S*)(\s*)(.*)$/);
  if (!nameMatch) {
    return {
      raw,
      name: '',
      args: [],
      rest: '',
      trailingSpace: false,
    };
  }

  const [, nameTokenRaw, spacing, remainder] = nameMatch;
  const name = nameTokenRaw.toLowerCase();
  const trailingSpace = spacing.length > 0;

  const rest = remainder.trim();
  const args = rest.length > 0 ? rest.split(/\s+/) : [];

  return {
    raw,
    name,
    args,
    rest,
    trailingSpace,
  };
}
